import { TranslationKey } from './../../../../types/translation/TranslationKey';
import React from 'react';
import { t } from '../../../../types/translation/Translator';
import { BaseFilter, BaseFilterProps } from '../common/BaseFilter';
import CategoryIcon from '@mui/icons-material/Category';
import { AssetTransaction } from '../../../../types/asset_transaction/assetTransaction';
import { ProductMasterData } from '../../../../types/product_master_data/productMasterData';
import { User } from '../../../../types/user';
import { FilterProps, FilterStorageKey } from '../common/filter.util';
import { AssetFilter } from '../AssetFilter/AssetFilter';
import { AssignedToFilter } from '../AssignedToFilter/AssignedToFilter';
import { AssetTransactionStatusFilter } from './Common/AssetTransactionFilter/AssetTransactionStatusFilter';
import { AssetCreatedAtFilter } from './Common/AssetCreatedAtFilter/AssetCreatedAtFilter';
import { AssetSignedAtFilter } from './Common/AssetSignedAtFilter/AssetSignedAtFilter';
import { Asset } from '../../../../types/asset/asset';
import { AssetTransactionUtil } from '../../../../types/asset_transaction/assetTransaction.util';
import { CustomField, CustomFieldEntityType } from '../../../../types/custom_field/customField';
import { itemsContainText, toFilterString } from '../../../../util/string.util';
import { ProductMasterDataFilter } from '../ProductMasterDataFilter';
import { TagRelation } from '../../../../types/tag';
import { TableSettings } from '../../../../types/table_settings/TableSettings';
import { Company } from '../../../../types/company';
import { CompanyPartnerFilter } from '../CompanyPartnerFilter/CompanyPartnerFilter';
import { CustomFieldFilter } from '../CustomFieldFilter';

export class AssetTransactionFilter extends BaseFilter<AssetTransaction> {
  productMasterData: Map<string, ProductMasterData>;
  users: Map<string, User>;
  assets: Map<string, Asset>;
  assetTransactions: Map<string, AssetTransaction>;
  assetTableSettings: TableSettings<any>;
  customFields: Map<string, CustomField>;
  tagRelations: Map<string, TagRelation>;
  company: Company;

  constructor(
    productMasterData: Map<string, ProductMasterData>,
    users: Map<string, User>,
    assets: Map<string, Asset>,
    assetTransactions: Map<string, AssetTransaction>,
    assetTableSettings: TableSettings<any>,
    customFields: Map<string, CustomField>,
    tagRelations: Map<string, TagRelation>,
    company: Company,
  ) {
    super(FilterStorageKey.ASSET_TRANSACTION);
    this.productMasterData = productMasterData;
    this.users = users;
    this.assets = assets;
    this.assetTransactions = assetTransactions;
    this.assetTableSettings = assetTableSettings;
    this.customFields = customFields;
    this.tagRelations = tagRelations;
    this.company = company;
  }

  filterOptions = () => [
    ...(this.company.partner ? [new CompanyPartnerFilter()] : []),
    new AssetFilter(
      this.productMasterData,
      this.users,
      this.assets,
      this.assetTransactions,
      this.assetTableSettings,
      this.customFields,
      this.tagRelations,
      this.company,
    ),
    new AssignedToFilter(this.users),
    new AssetTransactionStatusFilter(),
    new AssetCreatedAtFilter(),
    new AssetSignedAtFilter(),
    ...CustomFieldFilter.for(
      CustomFieldEntityType.asset,
      new Map([...this.assets.values()].map(asset => [asset.id, asset.product])),
      this.customFields,
    ),
    ...CustomFieldFilter.for(CustomFieldEntityType.productMasterData, this.productMasterData, this.customFields),
  ];

  toLabel = () => {
    return t(TranslationKey.asset).singular.label;
  };

  toCount(props: BaseFilterProps) {
    return 0;
  }

  toDropdownContent() {
    return <></>;
  }

  toInnerContent = () => {
    return <></>;
  };

  toIcon = () => {
    return <CategoryIcon className={this.className} />;
  };

  search(item: AssetTransaction, query: string) {
    const productName = this.productMasterData.get(item.product.productMasterDataId)?.productName;
    const productNumber = this.productMasterData.get(item.product.productMasterDataId)?.productNumber;

    const user = this.users.get(item.location?.entityId || '');
    const firstName = user?.firstName;
    const lastName = user?.lastName;
    const email = user?.email;

    for (const field of item.customFieldValues()) {
      const value = toFilterString(field.value);
      if (value.includes(query)) return true;
    }

    return itemsContainText(query, [productName, productNumber, firstName, lastName, email]);
  }

  filteredItems(items: AssetTransaction[], query: string, filterProps: FilterProps) {
    return items.filter(item => {
      const productMasterData = this.productMasterData.get(item.product.productMasterDataId);
      return [
        AssetSignedAtFilter.filter(filterProps, item.processedAt ? new Date(item.processedAt) : undefined),
        AssetCreatedAtFilter.filter(filterProps, new Date(item.createdAt)),
        ProductMasterDataFilter.filter(filterProps, productMasterData, this.customFields, this.tagRelations),
        AssetTransactionFilter.filter(filterProps, item, this.customFields),
        this.search(item, query),
      ].every(value => value === null || value);
    });
  }

  static filter(filterProps: FilterProps, item?: AssetTransaction, customFields: Map<string, CustomField> = new Map()) {
    if (item && filterProps.company?.size && !filterProps.company.has(item.companyId)) return false;
    if (item && filterProps.user?.size && !filterProps.user.has(item.location?.entityId || '')) return false;
    if (item && filterProps.assetSignedAt && !item.processedAt) return false;
    if (
      item &&
      filterProps.assetTransactionStatus?.size &&
      !filterProps.assetTransactionStatus.has(AssetTransactionUtil.transactionToDetailedStatus(item))
    ) {
      return false;
    }

    return CustomFieldFilter.filter(filterProps, item?.product, customFields, CustomFieldEntityType.asset);
  }
}
